import React from "react";
import {CheckCircleOutlined, ExclamationCircleFilled, LockOutlined} from "@ant-design/icons";
import {Button, Col, Form, Input, Row} from "antd";
import i18next from "i18next";
import * as Setting from "../../conf/Setting";
import * as OrganizationBackend from "../../services/OrganizationBackend";
import * as Util from "../../utils/Util";
import * as PasswordPolicyBackend from "../../services/PasswordPolicyBackend";
import * as AuthBackend from "../../services/AuthBackend";
import {JSEncrypt} from "jsencrypt";
import * as UserBackend from "../../services/UserBackend";
import GlobalContext from "../../context/GlobalContext";

export const PasswordRemindPage = (props) => {
  const [form] = Form.useForm();
  const [organization, setOrganization] = React.useState(null);
  const [pwdPolicy, setPwdPolicy] = React.useState(null);
  const {account} = props;
  const [checkMsg, setCheckMsg] = React.useState("");
  const [skipChange, setSkipChange] = React.useState(false);
  const [redirectUri, setRedirectUri] = React.useState("");

  React.useEffect(() => {
    const organizationId = getOrganizationId();
    if (organizationId !== undefined) {
      getOrganization();
    } else {
      return Util.renderMessageLarge(this, Setting.getResponseMessage("组织不存在"), () => {
        Setting.goToLink("/");
      });
    }
    const params = new URLSearchParams(window.location.search);
    setCheckMsg(JSON.parse(params.get("msg")));
    setSkipChange(params.get("skip") === "1" ? true : false);
    setRedirectUri(Util.decodeUrl(params.get("redirect_uri")));
  }, []);

  const getOrganizationId = () => {
    return props.organizationId !== undefined
      ? props.organizationId
      : props.match === undefined
        ? null
        : props.match.params.organizationId;
  };

  const getOrganizationObj = () => {
    if (props.organization !== undefined) {
      return props.organization;
    } else {
      return organization;
    }
  };

  const getOrganization = () => {
    const organizationId = getOrganizationId();
    if (organizationId === null) {
      return;
    }

    OrganizationBackend.getOrganization(Setting.Admin, organizationId).then(
      (organizationRes) => {
        setOrganization(organizationRes);
        PasswordPolicyBackend.getConfig(organizationRes.owner, organizationRes.pwdConfig)
          .then(resConfig => {
            if (resConfig.msg === undefined) {
              setPwdPolicy(resConfig);
            } else {
              Setting.showResponseMessage(resConfig.msg);
            }
          });
      }
    );
  };

  const onFinishFailed = (values, errorFields) => {};

  const onFinish = (values) => {
    const newPassword = values["newPassword"];
    AuthBackend.getPublicKey().then(resPubKey => {
      if (resPubKey.status === "ok") {
        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(resPubKey.msg);
        const newEncryptPassword = encrypt.encrypt(newPassword);
        const organizationObj = getOrganizationObj();

        PasswordPolicyBackend.checkPassword(organizationObj.owner, organizationObj.uid, account.owner, account.name, newEncryptPassword)
          .then(resCheck => {
            if (resCheck.status === "ok") {
              UserBackend.setPassword(organizationObj.uid, account.name, "", newEncryptPassword).then(res => {
                if (res.status === "ok") {
                  if (!redirectUri) {
                    Setting.goToLink("/");
                  } else {
                    Setting.goToLink(redirectUri);
                  }
                } else {
                  Setting.showResponseMessage(res.msg);
                }
              });
            } else {
              Setting.showResponseMessage(resCheck.msg);
            }
          });
      } else {
        Setting.showResponseMessage(resPubKey.msg);
      }
    });
  };

  return (
    <GlobalContext.Consumer>
      {({passwordCheckMsg, skipChangePassword, updateSkipPasswordCheck}) => (
        <Row>
          <Col span={24} style={{justifyContent: "center", marginTop: 80}}>
            <Row justify="center" style={{fontSize: 24}}>
              {i18next.t("password:Password change remind")}
            </Row>
            {!checkMsg ?
              null :
              <Row justify="center" style={{marginTop: 16}}>
                <div style={{textAlign: "left", color: "#FFAA00", background: "#FFF0D3", padding: "4px 10px"}}>
                  <ExclamationCircleFilled style={{color: "#FFAA00", marginRight: 10}} />
                  {Setting.getResponseMessage(checkMsg)}
                </div>
              </Row>}
          </Col>
          <Col span={24} style={{display: "flex", justifyContent: "center"}}>
            <div style={{marginTop: "10px", textAlign: "center"}}>
              <Form
                form={form}
                onFinish={onFinish}
                name="changePassword"
                onFinishFailed={(errorInfo) =>
                  onFinishFailed(
                    errorInfo.values,
                    errorInfo.errorFields,
                    errorInfo.outOfDate
                  )
                }
                initialValues={{
                  organization: getOrganizationId(),
                }}
                style={Setting.isMobile() ? {} : {width: 500}}
                size="large"
                labelCol={{span: 5}}
                wrapperCol={{span: 19}}
              >
                <Form.Item
                  style={Setting.isMobile() ? {height: 0, visibility: "hidden", marginBottom: 12}
                    : {height: 0, visibility: "hidden"}}
                  name="organization"
                  rules={[
                    {
                      required: true,
                      message: i18next.t(
                        "forget:Please input your organization!"
                      ),
                    },
                  ]}
                />
                <Form.Item
                  label={i18next.t("password:New password")}
                  name="newPassword"
                  rules={[
                    {
                      required: true,
                      message: i18next.t(
                        "login:Please input your password!"
                      ),
                    },
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder={i18next.t("forget:Password")}
                  />
                </Form.Item>
                <Form.Item
                  label={i18next.t("password:Password confirm")}
                  name="confirm"
                  dependencies={["newPassword"]}
                  hasFeedback
                  style={Setting.isMobile() ? {marginBottom: 12} : {marginBottom: 12}}
                  rules={[
                    {
                      required: true,
                      message: i18next.t(
                        "signup:Please confirm your password!"
                      ),
                    },
                    ({getFieldValue}) => ({
                      validator(rule, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          i18next.t(
                            "signup:Your confirmed password is inconsistent with the password!"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<CheckCircleOutlined />}
                    placeholder={i18next.t("forget:Confirm")}
                  />
                </Form.Item>
                <Row>
                  <Col offset={Setting.isMobile() ? 0 : 4}>
                    <div style={{fontSize: Setting.isMobile() ? 12 : 14, color: "#999999", textAlign: "left", marginBottom: 12}}>
                      {Setting.getPasswordRuleText(pwdPolicy)}
                    </div>
                  </Col>
                </Row>
                <br />
                <Row>
                  <Col span={Setting.isMobile() ? 24 : 19} offset={Setting.isMobile() ? 0 : 5}>
                    {
                      !skipChange ?
                        <Form.Item style={{width: "100%"}}>
                          <Row>
                            <Col span={24}>
                              <Button block type="primary" htmlType="submit" disabled={false}>
                                {i18next.t("password:Confirm")}
                              </Button>
                            </Col>
                          </Row>
                        </Form.Item>
                        :
                        <Form.Item style={{width: "100%"}}>
                          <Row>
                            <Col span={11}>
                              <Button block type="default" disabled={false} onClick={() => {
                                updateSkipPasswordCheck(true, () => {});
                                if (!redirectUri) {
                                  Setting.goToLink("/");
                                } else {
                                  Setting.goToLink(redirectUri);
                                }
                              }}>
                                {i18next.t("password:Skip")}
                              </Button>
                            </Col>
                            <Col span={11} offset={2}>
                              <Button block type="primary" htmlType="submit" disabled={false}>
                                {i18next.t("password:Confirm")}
                              </Button>
                            </Col>
                          </Row>
                        </Form.Item>
                    }
                  </Col>
                </Row>
              </Form>
            </div>
          </Col>
        </Row>
      )}
    </GlobalContext.Consumer>);
};

export default PasswordRemindPage;
